import { AxiosError } from 'axios';
import { API_URL } from './apiDictionary';
import { RESTClient } from './client';
import { replacePathParams } from 'src/utils/replacePathWithParams';
import {
  QCTVisitDataDTO,
  QCTVisitLabeledScore,
  QCTWorkstationDataDTO,
} from '@dynamika-shared/types/QCTReading';
import { WorkflowExecution } from '@dynamika-shared/types/executions';

const clinicalTrialHeaders = (clinicalTrialId: string) => ({
  headers: {
    'x-dynamika-clinical-trial-id': clinicalTrialId,
  },
});

/** Results already selected for reading on this visit. */
export const fetchQCTVisitData = async (
  clinicalTrialId: string,
  taskId: string
) => {
  try {
    const response = await RESTClient.get<QCTVisitDataDTO>(
      replacePathParams(API_URL.QCTVisitData, { taskId }),
      clinicalTrialHeaders(clinicalTrialId)
    );

    // 204 and 404 both mean "nothing selected yet" — an empty visit, not a
    // failure. Only real transport/server errors are allowed to propagate.
    if (response.status === 204) {
      return null;
    }

    return response.data;
  } catch (error) {
    if ((error as AxiosError).response?.status === 404) {
      return null;
    }

    console.error('Error fetching fetchQCTVisitData', error);
    throw error;
  }
};

/** Everything the last workstation query returned, one entry per record. */
export const fetchQCTWorkstationData = async (
  clinicalTrialId: string,
  taskId: string
) => {
  const response = await RESTClient.get<QCTWorkstationDataDTO[]>(
    replacePathParams(API_URL.QCTWorkstationData, { taskId }),
    clinicalTrialHeaders(clinicalTrialId)
  );

  // Empty means "nothing synchronised yet", not a failure: a 204 leaves
  // `data` as `''`, which would throw on `.map` mid-render. Real errors still
  // propagate for the saga to toast.
  if (!Array.isArray(response.data)) {
    return [];
  }

  return response.data;
};

export const syncQCTWorkstationReadings = async (
  clinicalTrialId: string,
  taskId: string
) => {
  const response = await RESTClient.post<WorkflowExecution>(
    replacePathParams(API_URL.QCTQuery, { taskId }),
    undefined,
    clinicalTrialHeaders(clinicalTrialId)
  );

  return response.data;
};

/**
 * Writes the visit's selection back.
 *
 * Plain terms
 * -----------
 * The specialist's whole list of chosen results is saved in one go — the
 * backend replaces what it holds with what is sent, so anything left out is
 * dropped from the visit.
 *
 * Technical
 * ---------
 * `PUT /qc/tasks/:taskId/qct-visit-data` takes a `QctVisitDataDto`, i.e. the
 * labelled scores wrapped in a `data` envelope — a bare array is rejected.
 * There is no per-result endpoint, which is why every caller in
 * `QCTReadings.saga.ts` rebuilds the full list before calling this.
 *
 * The stored document is returned so the caller can publish it straight into
 * the slice; a 204 or an empty body leaves `data` as `''`, in which case what
 * was just written is the truth and is handed back instead.
 */
export const putQCTWorkstationDataToVisit = async (
  clinicalTrialId: string,
  taskId: string,
  data: QCTVisitLabeledScore[]
) => {
  const response = await RESTClient.put<QCTVisitDataDTO>(
    replacePathParams(API_URL.QCTVisitData, { taskId }),
    { data },
    clinicalTrialHeaders(clinicalTrialId)
  );

  if (!Array.isArray(response.data?.data)) {
    return { data };
  }

  return response.data;
};
